/**
 * My Services Widget
 *
 * Displays services owned by user's teams
 */

import { useState, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import { useMyServices } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { WidgetConfigDialog } from './WidgetConfigDialog';
import { useWidgetPolling } from './useWidgetPolling';
import { StatusBadge } from '../shared/StatusBadge';
import { WidgetTagFilterEditor } from './WidgetTagFilterEditor';
import { TextField } from '../forms/TextField';
import type { WidgetConfig, WidgetState } from './types';
import type { Service } from '../../graphql/types';
import type { DashboardWidgetFilter } from '../../types/preferences';
import '@tylertech/forge/list';
import '@tylertech/forge/icon';
import '@tylertech/forge/icon-button';
import './MyServicesWidget.css';

const DEFAULT_TITLE = 'My Services';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'my-services',
  title: DEFAULT_TITLE,
  size: 'medium',
  refreshInterval: 30000, // 30 seconds
  showRefresh: true,
  showExpand: false,
};

export interface MyServicesWidgetProps {
  /** Custom display name overriding the catalog title (issue #489). */
  title?: string;
  onTitleChange?: (title: string | undefined) => void;
  /** Tag/category filter scoping this widget's data (issue #489). */
  filters?: DashboardWidgetFilter;
  onFiltersChange?: (filters: DashboardWidgetFilter | undefined) => void;
}

export function MyServicesWidget({
  title,
  onTitleChange,
  filters,
  onFiltersChange,
}: MyServicesWidgetProps = {}): JSX.Element {
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [configOpen, setConfigOpen] = useState(false);

  // Fetch ownership-scoped services via GraphQL, optionally narrowed by the
  // widget's tag/category filter (issue #489), resolved server-side via GSI6.
  const { data, loading, error, refetch } = useMyServices({
    tagFilters: filters?.tagFilters,
    filterOperator: filters?.operator,
  });

  // Widget state
  const widgetState: WidgetState<Service[]> = {
    data: data?.myServices?.edges?.map((edge) => edge.node) || null,
    loading,
    error: error?.message || null,
    lastRefresh,
  };

  // Refresh handler
  const handleRefresh = useCallback(() => {
    refetch();
    setLastRefresh(new Date());
  }, [refetch]);

  // Auto-refresh polling
  useWidgetPolling({
    interval: WIDGET_CONFIG.refreshInterval!,
    onRefresh: handleRefresh,
    enabled: !loading && !error,
  });

  // Update last refresh on successful data load
  if (data && !lastRefresh) {
    setLastRefresh(new Date());
  }

  const services = widgetState.data || [];
  const ListElement = 'forge-list' as React.ElementType;
  const ListItemElement = 'forge-list-item' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const IconButtonElement = 'forge-icon-button' as React.ElementType;

  const effectiveConfig: WidgetConfig = { ...WIDGET_CONFIG, title: title || DEFAULT_TITLE };

  const configureAction = onTitleChange && onFiltersChange && (
    <IconButtonElement
      density="dense"
      onClick={() => setConfigOpen((open) => !open)}
      aria-label="Configure widget"
      data-testid="my-services-widget-configure"
    >
      <IconElement name="settings" />
    </IconButtonElement>
  );

  return (
    <>
      <DashboardWidget
        config={effectiveConfig}
        state={widgetState}
        onRefresh={handleRefresh}
        headerAction={configureAction}
      >
        {services.length === 0 ? (
          <div className="my-services-widget__empty">
            <IconElement name="cloud_off" className="my-services-widget__empty-icon" />
            <p className="forge-typography--body2">No services owned by you or your teams</p>
            <Link to="/services/new" className="forge-button forge-button--raised">
              Create Service
            </Link>
          </div>
        ) : (
          <div className="my-services-widget__content">
            <ListElement className="my-services-widget__list">
              {services.slice(0, 10).map((service) => (
                <ListItemElement key={service.entityId} className="my-services-widget__list-item">
                  <Link
                    to="/services/$serviceId"
                    params={{ serviceId: service.entityId }}
                    className="my-services-widget__link"
                  >
                    <div className="my-services-widget__item">
                      <div className="my-services-widget__item-main">
                        <IconElement name="cloud" aria-hidden="true" />
                        <div className="my-services-widget__item-text">
                          <span className="forge-typography--label1">{service.name}</span>
                          {service.description && (
                            <span className="forge-typography--caption my-services-widget__item-description">
                              {service.description}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="my-services-widget__item-badges">
                        <StatusBadge variant="lifecycle" value={service.lifecycleStatus} />
                        <StatusBadge variant="tier" value={service.tier} />
                      </div>
                    </div>
                  </Link>
                </ListItemElement>
              ))}
            </ListElement>

            {services.length > 10 && (
              <div className="my-services-widget__footer-action">
                <Link
                  to="/services"
                  search={{
                    page: 1,
                    pageSize: 25,
                    search: '',
                    lifecycleStatus: '',
                    tier: '',
                    tagId: '',
                    teamId: '',
                    technologyId: '',
                  }}
                  className="forge-button forge-button--text"
                >
                  View all {services.length} services
                  <IconElement name="arrow_forward" slot="end" />
                </Link>
              </div>
            )}
          </div>
        )}
      </DashboardWidget>

      {onTitleChange && onFiltersChange && (
        <WidgetConfigDialog
          open={configOpen}
          title={`Configure ${effectiveConfig.title}`}
          onClose={() => setConfigOpen(false)}
        >
          {/* Mount the form (and its useTagCategories query) only once the
           * dialog has actually been opened — matches the prior inline
           * panel's lazy-mount behavior and keeps widgets that render
           * MyServicesWidget without opening config from needing to mock
           * WidgetTagFilterEditor's data dependency. */}
          {configOpen && (
            <div data-testid="my-services-widget-config-panel">
              <TextField
                name="my-services-widget-title"
                label="Widget name"
                placeholder={DEFAULT_TITLE}
                value={title ?? ''}
                onChange={(e) => onTitleChange(e.target.value.trim() ? e.target.value : undefined)}
              />
              <WidgetTagFilterEditor filters={filters} onChange={onFiltersChange} />
            </div>
          )}
        </WidgetConfigDialog>
      )}
    </>
  );
}
